/**
 * DIE MAILTEXTE BEARBEITEN — MIT DER PLATZHALTER-LEISTE ZUM ANKLICKEN (Punkt 82).
 *
 * Antoine am 20.09.2026, wörtlich: „…dazu immer der Mail Text mit variablen, also mit Platzhalter,
 * was dann dort steht und **eine Liste mit Platzhalter, die ich per click da hinsetzen kann wo ich
 * gerade mit'm Cursor in der Mail bin**, damit sich jeder diese Mail selber zusammenstellen kann."
 *
 * Und am 22.09.2026, mit Bildschirmfoto: „Was hier fehlt sind die Inhalte der einzelnen Mails."
 *
 * ── DREI ENTSCHEIDUNGEN, DIE MAN SONST NICHT SIEHT ──────────────────────────────────────────
 *
 * 1. **Die Leiste fügt dort ein, wo der Cursor ZULETZT war — in Betreff ODER Text.** Ein
 *    Platzhalter-Knopf nimmt dem Feld den Fokus; ohne gemerkten Ort landete alles stur im Text,
 *    und der Betreff wäre nur von Hand zu füllen. Deshalb merkt sich diese Komponente das zuletzt
 *    berührte Feld (`letztesFeld`). Voreinstellung ist der Text — das ist der häufigere Fall.
 *
 * 2. **Nicht gespeichert heißt Werkstext, nicht leer.** Beim Laden steht der Werkstext in den
 *    Feldern, wenn der Betrieb noch nichts geschrieben hat. Der Knopf „Auf Werkstext zurücksetzen"
 *    füllt die Felder wieder damit — er speichert nicht von selbst, damit ein Fehlgriff nichts
 *    kostet.
 *
 * 3. **Gespeichert wird je Mail, nicht alles auf einmal.** Der Server legt den Ausschnitt über
 *    den Bestand (`routes/settings.ts`); ein zweiter geöffneter Reiter kann so nicht die Arbeit
 *    des ersten zurückdrehen.
 *
 * 4. **Deutsch und Englisch je eigener Text** (Rest R5, Antoine 23.09.2026). Die englische Fassung
 *    ist ab Werk LEER — leer heißt: Gäste mit englischem Angebot bekommen den festen englischen
 *    Text. Mitgeschickt wird nur, was sich geändert hat: wer nur Englisch schreibt, verwandelt den
 *    nie gespeicherten deutschen Text nicht nebenbei in einen gespeicherten.
 */
import { useRef, useState } from "react";
import { insertAtCursor } from "./TextbausteinPicker";
import { Button, Icon, Input } from "./components";

export interface VorlagenBeschreibung {
  schluessel: string;
  bezeichnung: string;
  anlass: string;
  empfaenger: "gast" | "betrieb";
  werkBetreff: string;
  werkText: string;
  /** Punkt 82 (Rest): welche Platzhalter der Gruppe „Diese Mail“ diese Mail füllt. */
  mailPlatzhalter?: string[];
  /** Rest R5: bietet diese Mail eine englische Fassung an? */
  englisch?: boolean;
}

/** Was je Mail gespeichert ist — deutsch und (Rest R5) englisch. */
export interface GespeicherteMailVorlage {
  betreff?: string | null;
  text?: string | null;
  betreff_en?: string | null;
  text_en?: string | null;
}

/** Was beim Speichern hinausgeht: nur die Felder, die sich geändert haben (leer = löschen). */
export type VorlagenTeile = Partial<Record<"betreff" | "text" | "betreff_en" | "text_en", string>>;

export interface PlatzhalterBeschreibung {
  schluessel: string;
  beschriftung: string;
  gruppe: "immer" | "geld" | "anfrage" | "mail";
  hinweis: string;
}

const GRUPPEN_NAME: Record<PlatzhalterBeschreibung["gruppe"], string> = {
  immer: "Immer",
  geld: "Geld",
  anfrage: "Aus der Anfrage",
  mail: "Diese Mail",
};

export function MailVorlagenEditor({
  vorlage,
  platzhalter,
  gespeichert,
  canEdit,
  onSave,
}: {
  vorlage: VorlagenBeschreibung;
  platzhalter: PlatzhalterBeschreibung[];
  gespeichert?: GespeicherteMailVorlage;
  canEdit: boolean;
  onSave: (teile: VorlagenTeile) => Promise<void>;
}) {
  const deStart = { betreff: gespeichert?.betreff?.trim() || vorlage.werkBetreff, text: gespeichert?.text?.trim() || vorlage.werkText };
  const enStart = { betreff: gespeichert?.betreff_en?.trim() ?? "", text: gespeichert?.text_en?.trim() ?? "" };
  const [de, setDe] = useState(deStart);
  const [en, setEn] = useState(enStart);
  /** Rest R5: welche Fassung gerade in den Feldern steht. */
  const [sprache, setSprache] = useState<"de" | "en">("de");
  const aktuell = sprache === "de" ? de : en;
  const betreff = aktuell.betreff;
  const text = aktuell.text;
  const setBetreff = (wert: string) => (sprache === "de" ? setDe : setEn)((v) => ({ ...v, betreff: wert }));
  const setText = (wert: string) => (sprache === "de" ? setDe : setEn)((v) => ({ ...v, text: wert }));
  const [busy, setBusy] = useState(false);
  const [gesichert, setGesichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [offen, setOffen] = useState(false);

  const betreffRef = useRef<HTMLInputElement | null>(null);
  const textRef = useRef<HTMLTextAreaElement | null>(null);
  /** Siehe Kopf, Punkt 1: wohin ein Klick auf einen Platzhalter einfügt. */
  const [letztesFeld, setLetztesFeld] = useState<"betreff" | "text">("text");

  const deGeaendert = de.betreff !== deStart.betreff || de.text !== deStart.text;
  const enGeaendert = en.betreff !== enStart.betreff || en.text !== enStart.text;
  const geaendert = deGeaendert || enGeaendert;
  const istWerkstand = de.betreff === vorlage.werkBetreff && de.text === vorlage.werkText;
  const hatEnglisch = Boolean(en.betreff.trim() || en.text.trim());

  function einfuegen(schluessel: string) {
    const marke = `{{${schluessel}}}`;
    if (letztesFeld === "betreff") insertAtCursor(betreffRef.current, betreff, setBetreff, marke);
    else insertAtCursor(textRef.current, text, setText, marke);
  }

  async function speichere() {
    setBusy(true);
    setFehler(null);
    setGesichert(false);
    try {
      const teile: VorlagenTeile = {};
      if (deGeaendert) Object.assign(teile, { betreff: de.betreff, text: de.text });
      if (enGeaendert) Object.assign(teile, { betreff_en: en.betreff, text_en: en.text });
      await onSave(teile);
      setGesichert(true);
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border border-outline-variant/40 bg-surface-container-lowest p-4 dark:border-outline/30 dark:bg-surface-variant/5">
      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        aria-expanded={offen}
        className="flex w-full items-start justify-between gap-3 text-left"
      >
        <span className="min-w-0">
          <span className="block font-title-md text-title-md text-on-background dark:text-inverse-on-surface">
            {vorlage.bezeichnung}
          </span>
          <span className="mt-0.5 block font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            {vorlage.anlass}
          </span>
        </span>
        <span className="shrink-0 pt-0.5 font-label-md text-[12px] text-primary">
          {offen ? "Schließen" : geaendert || !istWerkstand || hatEnglisch ? "Bearbeiten (angepasst)" : "Bearbeiten"}
        </span>
      </button>

      {offen && (
        <div className="mt-4 space-y-3">
          {fehler && (
            <p className="rounded-md bg-error/10 px-3 py-2 font-body-sm text-body-sm text-error" role="alert">
              {fehler}
            </p>
          )}

          {vorlage.englisch && (
            <div className="flex gap-1.5" role="group" aria-label="Sprache der Fassung">
              {(
                [
                  ["de", "Deutsch"],
                  ["en", hatEnglisch ? "Englisch" : "Englisch (fester Text)"],
                ] as const
              ).map(([k, name]) => (
                <button
                  key={k}
                  type="button"
                  aria-pressed={sprache === k}
                  onClick={() => setSprache(k)}
                  className={`rounded-full border px-3 py-1 font-label-md text-[12px] transition-colors ${
                    sprache === k
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-outline-variant/60 text-on-surface-variant hover:bg-surface-container-high dark:border-outline/30 dark:hover:bg-white/5"
                  }`}
                >
                  {name}
                </button>
              ))}
            </div>
          )}
          {sprache === "en" && (
            <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
              Gilt für Gäste mit englischem Angebot. Lassen Sie Betreff und Text leer, bekommen sie den festen
              englischen Text von MAESTRO. Datum und Beträge setzt MAESTRO dann in englischer Schreibweise ein.
            </p>
          )}

          <label className="block">
            <span className="mb-1 block font-label-md text-label-md text-on-surface-variant dark:text-secondary-fixed-dim">Betreff</span>
            <Input
              ref={betreffRef}
              className="w-full"
              value={betreff}
              disabled={!canEdit || busy}
              onFocus={() => setLetztesFeld("betreff")}
              onChange={(e) => setBetreff(e.target.value)}
            />
          </label>

          <label className="block">
            <span className="mb-1 block font-label-md text-label-md text-on-surface-variant dark:text-secondary-fixed-dim">Text</span>
            <textarea
              ref={textRef}
              value={text}
              rows={12}
              disabled={!canEdit || busy}
              onFocus={() => setLetztesFeld("text")}
              onChange={(e) => setText(e.target.value)}
              className="w-full rounded-lg border border-outline-variant bg-surface-container-lowest px-3 py-2 font-body-md text-body-md text-on-background disabled:opacity-60 dark:border-outline/40 dark:bg-surface-variant/10 dark:text-inverse-on-surface"
            />
          </label>

          {/* DIE LEISTE. Ein Klick setzt den Platzhalter an die Cursor-Position des zuletzt
              berührten Feldes — siehe Kopf, Punkt 1. */}
          <div>
            <p className="font-label-md text-label-md text-on-surface-variant dark:text-secondary-fixed-dim">
              Platzhalter einfügen — landet dort, wo Sie zuletzt geschrieben haben
              {letztesFeld === "betreff" ? " (gerade: Betreff)" : " (gerade: Text)"}
            </p>
            {(["mail", "immer", "geld", "anfrage"] as const).map((gruppe) => {
              // „Diese Mail“ zeigt nur, was genau diese Mail füllt — sonst verschluckte ein Klick
              // auf „Rechnungsnummer“ in der Nachfass-Mail beim Versand die ganze Zeile.
              const dieser = platzhalter.filter(
                (p) => p.gruppe === gruppe && (gruppe !== "mail" || (vorlage.mailPlatzhalter ?? []).includes(p.schluessel)),
              );
              if (dieser.length === 0) return null;
              return (
                <div key={gruppe} className="mt-2">
                  <span className="font-label-md text-[11px] uppercase tracking-wide text-secondary dark:text-secondary-fixed-dim">
                    {GRUPPEN_NAME[gruppe]}
                  </span>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {dieser.map((p) => (
                      <button
                        key={p.schluessel}
                        type="button"
                        disabled={!canEdit || busy}
                        title={p.hinweis}
                        onClick={() => einfuegen(p.schluessel)}
                        className="rounded-full border border-outline-variant/60 bg-surface-container-lowest px-2.5 py-1 font-label-md text-[11.5px] text-on-surface-variant transition-colors hover:bg-surface-container-high disabled:opacity-50 dark:border-outline/30 dark:hover:bg-white/5"
                      >
                        {p.beschriftung}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            Fehlt bei einem Vorgang eine Angabe — kein Ort, keine Uhrzeit —, verschwindet die ganze
            Zeile aus der Mail. So steht dort nie ein leerer Doppelpunkt.
          </p>
          {vorlage.empfaenger === "gast" && vorlage.schluessel !== "anfrage_eingang" && (
            <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
              Knopf, Link, Grußformel, Signatur und Fußzeile setzt MAESTRO darunter selbst.
              {vorlage.englisch ? " Der deutsche Text gilt für deutschsprachige Gäste, die englische Fassung für Gäste mit englischem Angebot." : ""}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3 border-t border-outline-variant/30 pt-3 dark:border-outline/30">
            <Button type="button" className="!py-2" disabled={!canEdit || busy || !geaendert} onClick={() => void speichere()}>
              {busy ? "Speichert …" : "Speichern"}
            </Button>
            {sprache === "de" ? (
              <button
                type="button"
                disabled={!canEdit || busy || istWerkstand}
                onClick={() => {
                  setDe({ betreff: vorlage.werkBetreff, text: vorlage.werkText });
                  setGesichert(false);
                }}
                className="font-label-md text-label-md text-primary hover:underline disabled:opacity-50 disabled:no-underline"
              >
                Auf Werkstext zurücksetzen
              </button>
            ) : (
              <button
                type="button"
                disabled={!canEdit || busy || !hatEnglisch}
                onClick={() => {
                  setEn({ betreff: "", text: "" });
                  setGesichert(false);
                }}
                className="font-label-md text-label-md text-primary hover:underline disabled:opacity-50 disabled:no-underline"
              >
                Englische Fassung leeren (fester Text)
              </button>
            )}
            {gesichert && (
              <span className="inline-flex items-center gap-1 font-body-sm text-body-sm text-primary">
                <Icon name="check_circle" className="text-[18px]" /> Gespeichert
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
